import type { ReactNode } from "react";
import { ArrowUpRight, Globe, MapPin, MessageSquare } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { CompanyAvatar } from "./company-avatar";
import { UpvoteButton } from "./upvote-button";
import {
  addedAgo,
  getExtraDetails,
  getRowDetails,
  getSource,
  resolveRaise,
  type Application,
} from "./deal-fields";

/*
 * The deal row shared by the global Raiseboard, every fund board and every
 * lookbook.
 *
 * A list row, not a card: rows sit on hairline dividers inside a <BoardList>,
 * so a page of twenty companies reads as one calm index rather than twenty
 * boxes. The pages differ in what they fetch and where a row navigates, not
 * in how a deal looks.
 */

/** The list a page's rows sit in: an ink rule on top, hairlines between. */
export function BoardList({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <ol className={cn("border-t border-foreground", className)}>{children}</ol>
  );
}

export function BoardRow({
  application,
  rank,
  vote,
  onVote,
  onOpen,
  showSource = true,
  index = 0,
  trailing,
}: {
  application: Application;
  rank: number;
  /** Omitted on boards without endorsements (a fund's own raiseboard). */
  vote?: { voted: boolean; count: number };
  onVote?: () => void;
  onOpen: () => void;
  /** A row-level action beside the upvote, e.g. "email me this". */
  trailing?: ReactNode;
  /**
   * "From <board>" only earns its place where deals come from many workspaces.
   * A lookbook is one workspace's own selection, so it names the same board on
   * every row and is hidden there.
   */
  showSource?: boolean;
  /** Position on first paint, for a short staggered entrance. */
  index?: number;
}) {
  const { company, stageLabel, industries, bio, country } =
    getRowDetails(application);
  const extra = getExtraDetails(application);
  const source = getSource(application);
  const raise = resolveRaise(application);
  const name = company?.company_name ?? "Untitled";
  const stage = stageLabel && stageLabel !== "Unknown" ? stageLabel : null;

  return (
    <li
      className="group relative border-b border-border motion-safe:animate-rise"
      style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}
    >
      {/* The stretched overlay is the single "open" control, so the row stays a
          non-interactive container and the upvote / board link remain
          independently focusable (no nested interactive elements). */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${name}`}
        className="absolute inset-0 z-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30"
      />

      <div className="pointer-events-none relative z-10 grid grid-cols-[auto_1fr_auto] items-start gap-4 py-7 transition-transform duration-300 ease-out group-hover:translate-x-1.5 sm:grid-cols-[2.5rem_auto_1fr_9.5rem_auto] sm:gap-6">
        <span className="hidden pt-3.5 text-[13px] font-medium tabular-nums text-muted-foreground transition-colors duration-200 group-hover:text-foreground sm:block">
          {String(rank).padStart(2, "0")}
        </span>

        <CompanyAvatar
          size="xl"
          tone="soft"
          logoUrl={company?.logo_url}
          name={company?.company_name}
        />

        <div className="min-w-0">
          <h3 className="flex items-center gap-2 text-[20px] font-semibold leading-snug tracking-[-0.02em] sm:text-[21px]">
            <span className="truncate">{name}</span>
            {isNew(application) && <NewMark />}
            <ArrowUpRight
              aria-hidden
              className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100"
            />
          </h3>

          {bio && (
            <p className="mt-1.5 line-clamp-2 max-w-[60ch] text-[15px] leading-relaxed text-muted-foreground">
              {bio}
            </p>
          )}

          {/* Spaced, not dot-separated: on a narrow screen the run wraps, and
              a separator would end up leading the next line. */}
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
            {/* On mobile the raise column is hidden, so the raise leads here. */}
            {(raise || stage) && (
              <span className="font-medium tabular-nums text-foreground sm:hidden">
                {[raise, stage].filter(Boolean).join(" · ")}
              </span>
            )}
            {industries.slice(0, 2).map((t, i) => (
              <span key={t} className="inline-flex items-center gap-1.5">
                {i === 0 && <SectorDot industry={t} />}
                {t}
              </span>
            ))}
            {industries.length > 2 && (
              <span className="text-muted-foreground/70" title={industries.join(", ")}>
                +{industries.length - 2}
              </span>
            )}
            {country && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3 shrink-0" />
                {country}
              </span>
            )}
            {showSource &&
              source.name &&
              (source.slug && source.enabled ? (
                <a
                  href={`/rb/${source.slug}`}
                  target="_blank"
                  rel="noreferrer"
                  className="pointer-events-auto relative z-10 underline-offset-2 hover:text-foreground hover:underline"
                >
                  {source.name}
                </a>
              ) : (
                <span>{source.name}</span>
              ))}
            {!!application.comment_count && (
              <span className="inline-flex items-center gap-1 tabular-nums">
                <MessageSquare className="h-3 w-3 shrink-0" />
                {application.comment_count}
              </span>
            )}
          </div>
          <FactsLine extra={extra} />
        </div>

        <div className="hidden pt-1.5 text-right sm:block">
          <p
            className={cn(
              "tabular-nums tracking-[-0.02em]",
              raise ? "text-[19px] font-semibold" : "text-[15px] font-medium text-muted-foreground"
            )}
          >
            {raise ?? "Undisclosed"}
          </p>
          <p className="mt-0.5 text-[12px] text-muted-foreground">{stage ?? "Stage not set"}</p>
        </div>

        <div className="pointer-events-auto relative z-10 flex items-start gap-2">
          {trailing}
          {vote && (
            <UpvoteButton
              variant="outline"
              count={vote.count}
              voted={vote.voted}
              onClick={(e) => {
                e.stopPropagation();
                onVote?.();
              }}
            />
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * A sector's colour: one of a few muted hues, picked by the industry name so a
 * sector reads the same on every board. Colour that carries meaning, kept low
 * in chroma so it never competes with the companies themselves.
 */
const SECTOR_HUES = [262, 200, 160, 32, 340, 220, 120, 12];
function sectorColor(industry?: string): string | null {
  if (!industry) return null;
  let h = 0;
  for (let i = 0; i < industry.length; i++) h = (h * 31 + industry.charCodeAt(i)) >>> 0;
  return `hsl(${SECTOR_HUES[h % SECTOR_HUES.length]} 55% 50%)`;
}

function SectorDot({ industry }: { industry?: string }) {
  const color = sectorColor(industry);
  if (!color) return null;
  return (
    <span
      aria-hidden
      className="inline-block h-2 w-2 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

const NEW_WITHIN_DAYS = 14;

/** Added to the board in the last two weeks: worth a second look. */
function isNew(application: Application): boolean {
  const created = (application as { created_on?: string }).created_on;
  if (!created) return false;
  const age = Date.now() - new Date(created).getTime();
  return age >= 0 && age < NEW_WITHIN_DAYS * 86_400_000;
}

function NewMark() {
  return (
    <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-raising/30 px-2 py-px text-[11px] font-medium tracking-normal text-raising">
      New
    </span>
  );
}

/**
 * The grid view's cell: the same company, composed vertically. Cells sit in a
 * <BoardGrid>, which draws the hairlines between them, so a cell has no
 * border or shadow of its own.
 */
export function BoardCard({
  application,
  rank,
  vote,
  onVote,
  onOpen,
  showSource = true,
  index = 0,
  trailing,
}: {
  application: Application;
  rank: number;
  vote?: { voted: boolean; count: number };
  onVote?: () => void;
  onOpen: () => void;
  showSource?: boolean;
  index?: number;
  trailing?: ReactNode;
}) {
  const { company, stageLabel, industries, bio, country } =
    getRowDetails(application);
  const extra = getExtraDetails(application);
  const source = getSource(application);
  const raise = resolveRaise(application);
  const name = company?.company_name ?? "Untitled";
  const stage = stageLabel && stageLabel !== "Unknown" ? stageLabel : null;

  return (
    <li
      className="group relative bg-background transition-colors duration-200 hover:bg-card motion-safe:animate-rise"
      style={{ animationDelay: `${Math.min(index, 12) * 40}ms` }}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Open ${name}`}
        className="absolute inset-0 z-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring/30"
      />
      <div className="pointer-events-none relative z-10 flex h-full min-h-[19rem] flex-col p-6">
        <div className="flex items-start justify-between gap-3">
          <span className="flex items-center gap-2 pt-1 text-[13px] font-medium tabular-nums text-muted-foreground transition-colors duration-200 group-hover:text-foreground">
            {String(rank).padStart(2, "0")}
            {isNew(application) && <NewMark />}
          </span>
          <div className="pointer-events-auto relative z-10 flex items-start gap-2">
            {trailing}
            {vote && (
              <UpvoteButton
                variant="outline"
                count={vote.count}
                voted={vote.voted}
                onClick={(e) => {
                  e.stopPropagation();
                  onVote?.();
                }}
              />
            )}
          </div>
        </div>

        <CompanyAvatar
          size="xl"
          tone="soft"
          logoUrl={company?.logo_url}
          name={company?.company_name}
        />

        <h3 className="mt-5 flex items-center gap-2 text-[20px] font-semibold leading-snug tracking-[-0.02em]">
          <span className="truncate">{name}</span>
          <ArrowUpRight
            aria-hidden
            className="h-4 w-4 shrink-0 text-muted-foreground opacity-0 transition-opacity duration-200 group-hover:opacity-100"
          />
        </h3>
        {bio && (
          <p className="mt-2 line-clamp-3 text-[14.5px] leading-relaxed text-muted-foreground">
            {bio}
          </p>
        )}
        {(industries.length > 0 || (showSource && source.name)) && (
          <p className="mt-3 flex items-center gap-1.5 text-[12.5px] text-muted-foreground">
            <SectorDot industry={industries[0]} />
            <span className="truncate">
              {[industries.slice(0, 2).join(", "), showSource ? source.name : null]
                .filter(Boolean)
                .join("  ·  ")}
            </span>
          </p>
        )}

        {/* At least a breath above the facts, more when the cell is taller
            than its content. */}
        <div aria-hidden className="min-h-5 flex-1" />

        <dl className="grid grid-cols-2 gap-x-4 gap-y-3 border-t border-border/80 pt-4 text-[12.5px]">
          <div>
            <dt className="text-muted-foreground">Raising</dt>
            <dd
              className={cn(
                "mt-0.5 tabular-nums",
                raise ? "text-[16px] font-semibold tracking-[-0.01em]" : "font-medium text-muted-foreground"
              )}
            >
              {raise ?? "Undisclosed"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Stage</dt>
            <dd className="mt-0.5 font-medium">{stage ?? "Not set"}</dd>
          </div>
          <div className="min-w-0">
            <dt className="text-muted-foreground">Model</dt>
            <dd className="mt-0.5 truncate font-medium">
              {extra.businessModels.join(", ") || "Not set"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Founded</dt>
            <dd className="mt-0.5 font-medium tabular-nums">{extra.founded ?? "Not set"}</dd>
          </div>
        </dl>

        <div className="mt-4 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-[12.5px] text-muted-foreground">
          {country && (
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              {country}
            </span>
          )}
          {extra.websiteHref && (
            <a
              href={extra.websiteHref}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="pointer-events-auto relative z-10 inline-flex items-center gap-0.5 hover:text-foreground"
            >
              <Globe className="h-3 w-3" />
              {extra.websiteHost}
            </a>
          )}
          {!!application.comment_count && (
            <span className="inline-flex items-center gap-1 tabular-nums">
              <MessageSquare className="h-3 w-3" />
              {application.comment_count}
            </span>
          )}
          {addedAgo(extra.addedOn) && <span>Added {addedAgo(extra.addedOn)}</span>}
        </div>
      </div>
    </li>
  );
}

/** A grid cell's shape while the board loads. */
export function BoardCardSkeleton() {
  return (
    <li className="flex min-h-[19rem] flex-col bg-background p-6">
      <Skeleton className="h-3.5 w-8" />
      <Skeleton className="mt-4 h-14 w-14 rounded-xl" />
      <Skeleton className="mt-5 h-5 w-40" />
      <Skeleton className="mt-3 h-3.5 w-full" />
      <Skeleton className="mt-2 h-3.5 w-3/4" />
      <Skeleton className="mt-auto h-5 w-24" />
    </li>
  );
}

/** A row's shape while the list loads, so the page doesn't jump when it lands. */
export function BoardRowSkeleton() {
  return (
    <li className="flex items-start gap-4 border-b border-border py-7 sm:gap-6">
      <span className="hidden w-10 sm:block" />
      <Skeleton className="h-14 w-14 shrink-0 rounded-xl" />
      <div className="flex-1 space-y-2.5 pt-1">
        <Skeleton className="h-4 w-44" />
        <Skeleton className="h-3.5 w-full max-w-[52ch]" />
        <Skeleton className="h-3 w-56" />
      </div>
      <Skeleton className="h-11 w-11 shrink-0 rounded-lg" />
    </li>
  );
}

/**
 * A row's second line: how the company makes money, how old it is, where it
 * lives online and when it joined this board. Each fact is skipped when the
 * deal doesn't carry it, so a sparse deal stays one line.
 */
function FactsLine({ extra }: { extra: ReturnType<typeof getExtraDetails> }) {
  const short = (list: string[]) =>
    list.length > 2 ? `${list.slice(0, 2).join(", ")} +${list.length - 2}` : list.join(", ");
  const facts = [
    extra.businessModels.length > 0 && short(extra.businessModels),
    extra.revenueModels.length > 0 && short(extra.revenueModels),
    extra.founded && `Founded ${extra.founded}`,
  ].filter(Boolean) as string[];
  const added = addedAgo(extra.addedOn);
  if (facts.length === 0 && !extra.websiteHref && !added) return null;
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12.5px] text-muted-foreground/85">
      {facts.map((f) => (
        <span key={f}>{f}</span>
      ))}
      {extra.websiteHref && (
        <a
          href={extra.websiteHref}
          target="_blank"
          rel="noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="pointer-events-auto relative z-10 inline-flex items-center gap-1 hover:text-foreground"
        >
          <Globe className="h-3 w-3" />
          {extra.websiteHost}
        </a>
      )}
      {added && <span>Added {added}</span>}
    </div>
  );
}
